// The deck's logo: geometry, bounds, and the one padding rule (TODO §24.0.3, §24.2.2).
//
// Everything here is shared by the two renderers that draw the mark — `Slide.tsx` (which
// serves the editor, presentation, print and the offscreen thumbnail capturer in one) and
// `htmlExport.ts` (which serves the exported file). §24.4.2 is explicit about why: if the
// inset, the box and the padding reservation are typed twice they will drift, and the drift
// shows up as an exported deck whose logo sits somewhere else than the edited one.

import { SLIDE_W } from "@/lib/slide-size";
import type { LogoCorner } from "@/types/document";

// Everything here needs the mark's GEOMETRY and nothing else — not the asset it came from.
// Typed structurally so the export path can pass its already-baked logo (which has a data
// URL where the deck record has an assetId) to the very same helpers, which is the whole
// point of §24.4.2: one set of numbers, two renderers.
export interface LogoGeometry {
  corner: LogoCorner;
  sizePct: number;
}

export const LOGO_CORNERS: { value: LogoCorner; label: string }[] = [
  { value: "tl", label: "Top left" },
  { value: "tr", label: "Top right" },
  { value: "bl", label: "Bottom left" },
  { value: "br", label: "Bottom right" },
];

// §24.0.3 Size is a percentage of the LOGICAL canvas width, never a pixel count, so the
// mark scales with every render path for free — the same reason the page number is drawn
// inside the canvas rather than beside it (§7.1).
export const LOGO_SIZE_MIN = 3;
export const LOGO_SIZE_MAX = 15;
export const LOGO_SIZE_DEFAULT = 6;

// The canvas's own padding (`p-6` in `Slide.tsx`, `padding:24px` in the export stylesheet):
// the mark lines up with the slide's content rather than floating in the bleed.
export const LOGO_INSET_PX = 24;
// The canvas's row gap, reused as the breathing room between the mark and the text that
// yields to it.
export const LOGO_GAP_PX = 12;
// The box is 2:1 landscape, and the image is `contain`ed inside it. A FIXED box rather than
// `height: auto` is what makes §24.2.2's reservation computable at all: with an auto height
// the vertical extent depends on the file the user picked, so no renderer could know which
// rows the mark actually covers. A wide wordmark fills it; a square glyph sits centred in
// it at half the width, which is the correct relationship between the two.
export const LOGO_ASPECT = 0.5;

export function clampLogoSize(pct: number): number {
  if (!Number.isFinite(pct)) return LOGO_SIZE_DEFAULT;
  return Math.min(LOGO_SIZE_MAX, Math.max(LOGO_SIZE_MIN, Math.round(pct)));
}

/** The mark's box on the 1600×900 logical canvas, in canvas px. */
export function logoBox(sizePct: number): { width: number; height: number } {
  const width = (SLIDE_W * clampLogoSize(sizePct)) / 100;
  return { width, height: width * LOGO_ASPECT };
}

/**
 * §24.2.2 Every corner of the canvas is already occupied — on a content slide `tl` by the
 * title's first line, `tr` by the sticker, `bl` by the source, `br` by the page number; on a
 * cover, by one of the four corner fields (§19.0.2). So the row that shares the mark's side
 * takes a padding of the box plus a gap, and nothing else moves. It is a padding change on a
 * row, never a layout change.
 *
 * The reservation is stated by EDGE, not by row name, because the two layouts put different
 * rows at the same edge: a content slide stacks the title and the subtitle/sticker row at the
 * top, a cover has a single corner row there. Each renderer maps an edge to whichever of its
 * rows live on it — which is also why a top corner reserves on TWO rows in the content layout
 * and one on a cover. Reserving on only the title there means a long subtitle runs under the
 * mark at 12% and not at 6%, exactly the kind of bug that ships because it is invisible at the
 * default size.
 *
 * Returns canvas px, zero everywhere when there is no logo.
 */
export interface LogoReserve {
  /** For every row sitting at the TOP edge of the canvas. */
  topLeft: number;
  topRight: number;
  /** For every row sitting at the BOTTOM edge. */
  bottomLeft: number;
  bottomRight: number;
}

const NO_RESERVE: LogoReserve = {
  topLeft: 0,
  topRight: 0,
  bottomLeft: 0,
  bottomRight: 0,
};

export function logoReserve(logo: LogoGeometry | undefined): LogoReserve {
  if (!logo) return NO_RESERVE;
  const pad = logoBox(logo.sizePct).width + LOGO_GAP_PX;
  const top = logo.corner === "tl" || logo.corner === "tr";
  const left = logo.corner === "tl" || logo.corner === "bl";
  return {
    topLeft: top && left ? pad : 0,
    topRight: top && !left ? pad : 0,
    bottomLeft: !top && left ? pad : 0,
    bottomRight: !top && !left ? pad : 0,
  };
}

/** Where the box sits, as CSS offsets from the canvas edges. */
export function logoOffsets(corner: LogoCorner): {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
} {
  const vertical =
    corner === "tl" || corner === "tr"
      ? { top: LOGO_INSET_PX }
      : { bottom: LOGO_INSET_PX };
  const horizontal =
    corner === "tl" || corner === "bl"
      ? { left: LOGO_INSET_PX }
      : { right: LOGO_INSET_PX };
  return { ...vertical, ...horizontal };
}

// §24.2.3 A cover shows the deck logo too, in the same corner as every other slide — there
// is no `showsLogo` seam any more, and deliberately so. The mark is the deck's, and a title
// page is the slide people photograph; having it be the one page without the company's logo
// reads as an omission rather than as restraint. The cover's own corner field on that side
// yields to it through `logoReserve` like any other row, so the two do not collide.
